import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { usePrincipal } from "../auth/AuthContext";
import { ALL, mergeMoved, queryMatchesAreas, touchesInsights, type MovedAreas } from "./useLiveUpdates.areas";
import { LiveUpdatesManager, type LiveUpdatesManagerOptions } from "./useLiveUpdates.connection";
import { markLiveChangeApplied } from "./useLiveUpdates.state";
import { reconcileChanges, reconcileSnapshot, resetLiveVersions } from "./useLiveUpdates.versions";

export { useLiveConnectionState, getLiveConnectionState, subscribeLiveConnectionState } from "./useLiveUpdates.state";
export type { LiveConnectionState, LiveConnectionStatus, LiveTransport } from "./useLiveUpdates.state";

// Release 1.5.0 — changes are pushed to the browser instead of polled (O-R54). The tab keeps one connection; when the
// server says a data area moved, only the queries that read that area are refetched, and the existing "show what
// changed" highlight (components\motion) then marks exactly the rows and figures whose content changed.

/** True while the person is in the middle of something a refetch could disturb: an open dialog or a focused field. */
export function isBusy(): boolean {
  if (typeof document === "undefined") return false;
  if (document.querySelector('[role="dialog"], [role="alertdialog"], dialog[open]')) return true;
  const active = document.activeElement;
  if (!active) return false;
  const tag = active.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (active as HTMLElement).isContentEditable === true;
}

/** VIPER rewrites its insight text about 20 s after a change (warm-up debounce); its cards are refreshed once more then. */
export const INSIGHT_FOLLOW_UP_MS = 25_000;
/** While a change waits for the person to finish (dialog, field) or for the tab to be shown, how often to look again. */
export const IDLE_CHECK_MS = 1_000;

export interface LiveApplier {
  /** Refetch the queries that read these areas — now, or as soon as the person is not mid-task and the tab is shown. */
  apply(moved: MovedAreas): void;
  /** Try to apply anything waiting. */
  flush(): void;
  dispose(): void;
}

export interface LiveApplierDeps {
  isBusy?: () => boolean;
  isHidden?: () => boolean;
}

/**
 * Applies moved areas to the tab's queries with the D-O93 rule: never pull a page out from under someone mid-task
 * (an open dialog or a focused field) and never refetch for a hidden tab; what waits is applied once idle and visible.
 */
export function createLiveApplier(queryClient: QueryClient, deps: LiveApplierDeps = {}): LiveApplier {
  const busy = deps.isBusy ?? isBusy;
  const hidden = deps.isHidden ?? (() => typeof document !== "undefined" && document.visibilityState === "hidden");
  let pending: MovedAreas | null = null;
  let idleTimer: ReturnType<typeof setInterval> | null = null;
  let insightTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  const stopWatching = () => {
    if (idleTimer !== null) clearInterval(idleTimer);
    idleTimer = null;
  };

  const flush = () => {
    if (disposed || pending === null) return;
    if (hidden() || busy()) {
      if (idleTimer === null) idleTimer = setInterval(flush, IDLE_CHECK_MS);
      return;
    }
    const moved = pending;
    pending = null;
    stopWatching();
    void queryClient.invalidateQueries({ predicate: (q) => queryMatchesAreas(q.queryKey, moved), refetchType: "active" });
    markLiveChangeApplied();
    if (moved !== ALL && touchesInsights(moved)) {
      if (insightTimer !== null) clearTimeout(insightTimer);
      insightTimer = setTimeout(() => {
        insightTimer = null;
        if (!disposed) void queryClient.invalidateQueries({ queryKey: ["insights"], refetchType: "active" });
      }, INSIGHT_FOLLOW_UP_MS);
    }
  };

  const onIdleSignal = () => flush();
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onIdleSignal);
    document.addEventListener("focusout", onIdleSignal);
  }

  return {
    apply(moved) {
      if (moved !== ALL && moved.size === 0) return;
      pending = mergeMoved(pending, moved);
      flush();
    },
    flush,
    dispose() {
      disposed = true;
      stopWatching();
      if (insightTimer !== null) clearTimeout(insightTimer);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onIdleSignal);
        document.removeEventListener("focusout", onIdleSignal);
      }
    }
  };
}

/** Diagnostics only (not shown in the UI): `localStorage["summitops.live"] = "off"` keeps this tab on the 30-second check. */
export function liveSwitchedOffHere(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem("summitops.live") === "off";
  } catch {
    return false;
  }
}

// One connection per tab, whatever renders the hook (StrictMode mounts effects twice).
let shared: { manager: LiveUpdatesManager; users: number; identity: string } | null = null;
let currentApplier: LiveApplier | null = null;
let managerOptions: LiveUpdatesManagerOptions = {};

/** Tests: replace how connections are made (a fake hub). */
export function setLiveUpdatesManagerOptions(options: LiveUpdatesManagerOptions): void {
  managerOptions = options;
}

function acquire(identity: string): LiveUpdatesManager {
  if (shared && shared.identity !== identity) {
    void shared.manager.stop();
    shared = null;
    resetLiveVersions();
  }
  if (!shared) {
    const manager = new LiveUpdatesManager(
      {
        onSnapshot: (m) => currentApplier?.apply(reconcileSnapshot(m.nonce, m.areas ?? {})),
        onChanged: (m) => currentApplier?.apply(reconcileChanges(m.nonce, m.changes ?? []))
      },
      managerOptions
    );
    shared = { manager, users: 0, identity };
    manager.start();
  }
  shared.users += 1;
  return shared.manager;
}

function release(manager: LiveUpdatesManager): void {
  if (!shared || shared.manager !== manager) return;
  shared.users -= 1;
  if (shared.users <= 0) {
    void manager.stop();
    shared = null;
  }
}

/**
 * Opens this tab's push connection while <paramref name="enabled"/> (release 1.5.0 on and an Operations principal signed
 * in) and feeds what it hears to <paramref name="applier"/>. Re-opens for a different signed-in person; closes on sign-out.
 * The connection state is read with useLiveConnectionState().
 */
export function useLiveUpdates(enabled: boolean, applier: LiveApplier | null): void {
  const principal = usePrincipal();
  const identity = principal ? `${principal.authMode}|${principal.userPrincipalName ?? principal.displayName}` : null;

  useEffect(() => {
    if (!enabled || !applier || !identity || liveSwitchedOffHere()) return;
    currentApplier = applier;
    const manager = acquire(identity);
    const retry = () => manager.retryNow();
    const onVisible = () => {
      if (document.visibilityState === "visible") manager.retryNow();
    };
    window.addEventListener("online", retry);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", retry);
      document.removeEventListener("visibilitychange", onVisible);
      if (currentApplier === applier) currentApplier = null;
      release(manager);
    };
  }, [enabled, applier, identity]);
}
